import {
  EVENT_DESTROY,
  EVENT_DURATION_CHANGE,
  EVENT_EMPTY,
  EVENT_ERROR,
  EVENT_LEVELS_CHANGE,
  EVENT_LEVEL_CHANGE,
  EVENT_LOADING,
  EVENT_LOAD_START,
  EVENT_MUTE,
  EVENT_PAUSE,
  EVENT_PAUSED,
  EVENT_PLAY,
  EVENT_PLAYBACK_RATE_CHANGE,
  EVENT_PLAYING,
  EVENT_PLAYLIST_CHANGE,
  EVENT_PLAYLIST_ITEM_CHANGE,
  EVENT_PLAYLIST_LOAD,
  EVENT_POSITION,
  EVENT_PROGRESS,
  EVENT_RESUME,
  EVENT_SEEK,
  EVENT_SOURCE_CHANGE,
  EVENT_STATE,
  EVENT_STOP,
  EVENT_UNLOAD,
  EVENT_UNMUTE,
  EVENT_VOLUME_CHANGE,
  EVENT_WAITING,
  STATE_EMPTY,
  STATE_ERROR,
  STATE_IDLE,
  STATE_LOADING,
  STATE_PAUSED,
  STATE_PLAYING,
  STATE_WAITING,
  EVENT_LOADED,
  EVENT_COMPLETE,
  METADATA_TYPE_STREAM,
  METADATA_TYPE_ADSWIZZ_STREAM
} from './constants';
import LevelsController from './controllers/levels';
import PlaylistController from './controllers/playlist';
import TextTracksController from './controllers/text-tracks';
import TrackingController from './controllers/tracking';
import EventEmitter from './event-emitter';
import Environment from './utils/environment';
import PlayerError from './utils/errors';
import Events from '../utils/events';
import fileExtension from '../utils/file-extension';
import isObject from './utils/is-object';
import isString from './utils/is-string';
import logger from '../utils/logger';
import { getMimeFromExtension, isHlsMimeType } from './utils/mime-types';
import isIonoUrl from '../utils/is-iono-url';
import SourceLoader from './utils/source-loader';
import { canPlayFormat, canPlaySource, canPlayType } from './utils/source-support';
import round from '../utils/round';
import Hls from './controllers/hls';
import timeRangesToArray from './utils/timeranges-to-array';

// Events triggered by the HTML5 audio element that the player needs to proxy
const mediaEvents = [
  'loadstart', 'loadeddata', 'loadedmetadata', 'waiting', 'stalled', 'suspend',
  'play', 'pause', 'playing', 'seeking', 'seeked', 'ratechange', 'volumechange',
  'durationchange', 'timeupdate', 'progress', 'error', 'ended', 'canplay', 'canplaythrough',
  'emptied'
];

// A data-uri of a 0 second duration wav file used when the player gets emptied
const emptySourceUrl = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

/**
 * Checks if a source is an HLS media source
 *
 * @param {string|object} source
 *
 * @return {boolean}
 */
const isHlsSource = function (source) {
  if (isString(source)) {
    return isHlsMimeType(getMimeFromExtension(fileExtension(source)));
  }

  if (isObject(source) && source.type) {
    return isHlsMimeType(source.type);
  }

  if (isObject(source) && source.url) {
    return isHlsMimeType(getMimeFromExtension(fileExtension(source.url)));
  }
};

/**
 * Checks if the source is an empty source, used by the player to unload playback items
 *
 * @param {string} source
 *
 * @return {boolean}
 */
const isEmptySource = function (source) {
  return source === emptySourceUrl;
};

/**
 * Empties a player of its current playlist item
 */
const emptyPlaylistItem = function () {
  emptySource.call(this);

  this.playlist.deselectPlaylistItem();
  this.textTracks.clearTextTracks();
  this.tracking.clear();
  this.levels.clearLevels();

  // Reset player flags
  this._readyQueue = [];
  this._isLive = false;
  this._streamStartTime = null;
  this._supportedSources = false;
  this._hasPlayed = false;
  this._seekOnLoad = 0;
  this._model.duration = 0;
  this._model.currentTime = 0;
  this._model.paused = this._media.paused;
  this._durationChangeThreshold = 2;
};

/**
 * Empties a player of its current playback source
 */
const emptySource = function () {
  // Unset the audio source
  if (Hls.existsOn(this) && isHlsSource(this._model.currentSource)) {
    Hls.emptySource(this);
  } else if (this._media && this._media.src && !isEmptySource(this._media.src)) {
    this.stopLoad();
  }

  // Empty the loaded source
  this._model.source = false;
  this._model.currentSource = null;
  this._model.error = false;
  this._model.duration = 0;
  this._model.buffered = false;
  this._model.paused = true;
};

/**
 * Starts playback of the media element
 */
const play = function () {
  const success = _ => {
    // trigger the first play event on the first play
    if (!this._hasPlayed) {
      this._hasPlayed = true;
      return;
    }

    // otherwise trigger the resume event
    this.emit(EVENT_RESUME);
  };

  const error = e => {
    this._logger.warn(`Player: Error in play promise while starting playback: ${e.message}`);
    // transition back to a either the paused or idle state
    this.setState(this._canPlay ? STATE_PAUSED : STATE_IDLE);
  };

  try {
    this._model.paused = false;
    const playPromise = this._media.play();
    if (playPromise instanceof Promise) {
      playPromise
        .then(success)
        .catch(error);
    } else {
      success();
    }
  } catch (e) {
    error(e);
  }
};

/**
 * Pauses playback of the media element
 */
const pause = function () {
  const error = e => {
    this._logger.warn(`Player: Error attempting to pause playback: ${e.message}`);
  };

  try {
    this.emit(EVENT_PAUSE);
    if (this._state === STATE_PLAYING) {
      this.setState(STATE_PAUSED);
    }
    this._model.paused = true;

    const pausePromise = this._media.pause();
    if (pausePromise instanceof Promise) {
      pausePromise
        .catch(error);
    }
  } catch (e) {
    error(e);
  }
};

const MAX_STREAM_RECONNECT_ATTEMPTS = 3;

/**
 * Reconnect to a live stream
 *
 * @todo: if first stream reconnect attempt, start a timeout timer
 * @todo: when stream reconnect timeout timer fires, trigger stalled event and network error
 *
 * @param  {iono.Player} player Player to reconnect
 */
const streamReconnect = function streamReconnect () {
  if (this._streamReconnectAttempts++ >= MAX_STREAM_RECONNECT_ATTEMPTS) {
    this._streamReconnectAttempts = 0;
    this.setError(new PlayerError(PlayerError.NETWORK));
    return;
  }

  const resume = !this.isPaused();
  this.stopLoad();
  this.startLoad();
  if (resume) {
    this.play();
  }
};

const changeMode = function (player, mode) {
  if (player._mode === mode) return;

  if (mode === 'live') {
    player._mode = mode;
    player._isLive = true;
    player._model.duration = -1;

    // Force a normal playback rate on live audio
    if (player._media.playbackRate !== 1) {
      player._media.defaultPlaybackRate = 1;
      player._media.playbackRate = 1;

      player.emit({
        type: EVENT_PLAYBACK_RATE_CHANGE,
        playbackRate: player._media.playbackRate
      });
    }

    // player._media.crossOrigin = 'anonymous';
  } else if (mode === 'ondemand') {
    player._mode = mode;
    player._isLive = false;

    // Restore playback rate
    if (player._media.playbackRate !== player._model.playbackRate) {
      player._media.defaultPlaybackRate = player._model.playbackRate;
      player._media.playbackRate = player._model.playbackRate;

      player.emit({
        type: EVENT_PLAYBACK_RATE_CHANGE,
        playbackRate: player._media.playbackRate
      });
    }

    // player._media.crossOrigin = null;
  }
};

/**
 * @todo in setup, build a list of languages from the navigator.languages and navigator.language, then choose one as a defualt
 * @todo provide a means by which we can persist player settings to some form of local storage
 * @todo refactor the quality plugins to controllers
 */
class Player extends EventEmitter {
  constructor (options) {
    super();

    this.computed = options.computed;

    this._state = STATE_EMPTY;
    this._media = null;
    this._readyQueue = [];
    this._isLive = false;
    this._canPlay = false;
    this._supportedSources = false;
    this._playOnLoad = false;
    this._hasPlayed = false;
    this._seekOnLoad = 0;
    this._streamStartTime = null;
    this._streamReconnectAttempts = 0;
    this._logger = logger;
    this._loader = new SourceLoader(this, { logger });

    this._model = {
      paused: true,
      autoplay: false,
      preload: false,
      playbackRate: options.playbackRate,
      volume: options.volume,
      muted: options.muted,
      duration: 0,
      currentTime: 0,
      source: false,
      currentSource: null,
      error: null
    };

    Object.defineProperty(this, 'loaded', {
      get () {
        return this._media.readyState === 3 || this._media.readyState === 4;
      },
      enumerable: false,
      configurable: false
    });

    this.setup(options);
  }

  /**
   * Initialize the player
   *
   * @param {object} options
   */
  setup (options) {
    this._options = options;

    // update the logger
    this._logger = options.logger || logger;

    // @todo move the props from _model to the root of the player
    this._model.autoplay = options.autoplay;
    this._model.preload = options.preload;
    this._model.playbackRate = options.playbackRate;
    this._model.volume = options.volume;
    this._model.muted = options.muted;

    // Initialize controllers
    this.textTracks = new TextTracksController(this, options);
    this.textTracks._listeners = this._listeners;
    this.textTracks._observers = this._observers;

    this.playlist = new PlaylistController(this, options);
    this.tracking = new TrackingController(this, options);
    this.levels = new LevelsController(this, options);

    const _playlistEventHandler = (event) => {
      const playlistIndex = this.playlist.getPlaylistIndex();
      const playlistItem = this.playlist.getPlaylistItem();

      this._hasPlayed = false;

      // Trigger an event on the player corresponding to the playlist event
      if (event.type === PlaylistController.EVENT_CHANGE) {
        this.emit({
          type: EVENT_PLAYLIST_CHANGE,
          playlist: this.playlist.getPlaylist(true),
          added: event.added,
          loaded: event.loaded,
          total: event.total
        });
      }

      if (this._addingPlaylistItems) {
        this._addingPlaylistItems = false;
        return;
      }

      if (event.type === PlaylistController.EVENT_SELECT) {
        this.emit({
          type: EVENT_PLAYLIST_ITEM_CHANGE,
          playlistItem: playlistItem,
          playlistIndex: playlistIndex
        });
      }

      // load the selected playlist item in to the player as long as one is selected
      if (playlistIndex !== false) {
        this.loadSource(playlistItem);
      }
      // empty the current playlist item from the player if no playlist item is selected
      else {
        emptySource.call(this);
      }
    };

    const _levelsEventHandler = (event) => {
      if (event.type === LevelsController.EVENT_CHANGE) {
        this.emit({
          type: EVENT_LEVELS_CHANGE,
          levels: event.levels,
          level: event.level
        });

        // load the first source in the chosen level
        if (event.level) {
          this.setSource(event.level.sources[0]);
        }
        // if there are no levels to be played then there won't be a playable
        // source for this playlist item and so we trigger an error right away
        else {
          this.setError(new PlayerError(PlayerError.NO_PLAYABLE_SOURCES));
        }
      } else if (event.type === LevelsController.EVENT_SELECT) {
        // load the first source in the chosen level
        if (event.level) {
          const resume = !this.isPaused();
          const resumeFrom = this.getPosition();

          // Update the current playback source
          this.setSource(event.level.sources[0]);

          this.emit({
            type: EVENT_LEVEL_CHANGE,
            level: event.level
          });

          // Resume playback after changing the current playlist item
          if (resume) {
            this.startLoad();
            // call play directly on the media element to skip emitting the play event,
            // which will cause a resume tracking event to fire.
            this._media.play();
          }

          // Resume playback from the previous position before quality change
          if (resumeFrom > 0) {
            this.setPosition(resumeFrom);
          }
        }
      } else {
        emptySource.call(this);
      }
    };

    // load a new playback source whenever the current playlist or playlist item is changed
    Events.on(this.playlist, [
      PlaylistController.EVENT_CHANGE,
      PlaylistController.EVENT_SELECT
    ], _playlistEventHandler);

    // update the current playback source when the current levels or level changes
    Events.on(this.levels, [
      LevelsController.EVENT_CHANGE,
      LevelsController.EVENT_SELECT
    ], _levelsEventHandler);

    this.setupAudio(options);
    this.setupHls(options);
  }

  /**
   * Initialize the audio element on the player
   *
   * @param {Object} options
   */
  setupAudio (options) {
    const media = options.media || document.createElement('audio');

    media.autoplay = options.autoplay;
    media.preload = options.preload;
    media.defaultPlaybackRate = media.playbackRate = options.playbackRate;
    media.volume = options.volume;
    media.muted = options.muted;
    media.crossOrigin = null;

    Events.on(media, mediaEvents, this._logger.wrap(event => {
      this.handleEvent(event);
    }));

    this._media = media;

    return media;
  }

  /**
   * Initialise HLS support
   *
   * @param {object} options
   */
  setupHls (options) {
    // Disable Hls.js
    if (options.hlsjs === false || !Hls.isSupported()) {
      return;
    }

    Hls.init(this, options.hlsjs);
  }

  /**
   * Loads a new source for playback
   *
   * @param {object|array|string} source
   */
  load (source, options) {
    this._addingPlaylistItems = false;

    // empty the current playlist item
    emptyPlaylistItem.call(this);

    // transition to the loading state
    this.setState(STATE_LOADING);

    this.emit({
      type: EVENT_PLAYLIST_LOAD,
      mode: 'load'
    });

    const _loadSuccess = (source) => {
      if (source === false) {
        return this.setError(new PlayerError(PlayerError.SRC_INVALID));
      }

      // change the current playlist
      this.playlist.setPlaylist(source);

      // load the first playlist item for playback
      this.playlist.selectPlaylistItem(0);

      // this.emit(EVENT_LOADED);

      // // If the playOnLoad flag is set then begin playback now
      // if (this._playOnLoad) {
      //   this._logger.log('Player: Start playback once playlist item has loaded', 'debug2');
      //   this._playOnLoad = false;
      //   this.play();
      // }
    };

    const _loadError = (error) => {
      this.setError(new PlayerError(PlayerError.PLAYLIST, error.message));
    };

    this._loader.load(source, options)
      .then(_loadSuccess, _loadError);
  }

  /**
   * Loads a new source for playback
   *
   * @param {object|array|string} source
   */
  add (source, options) {
    this._addingPlaylistItems = true;
    const resumeState = this.getState();
    this.emit({
      type: EVENT_PLAYLIST_LOAD,
      mode: 'add'
    });

    const _loadSuccess = (source) => {
      // concatenate the loaded playlist items to the existing playlist
      this.playlist.addPlaylistItems(source);

      // If the playOnLoad flag is set then begin playback now
      if (this._playOnLoad) {
        this._playOnLoad = false;
        this.play();
      } else {
        this.setState(resumeState);
      }
    };

    const _loadError = (error) => {
      this._logger.warn(`Failed to load additional paylist items: ${error}`);
      this.setState(resumeState);
    };

    this._loader.load(source, options)
      .then(_loadSuccess)
      .catch(_loadError);
  }

  /**
   * Unloads the currently loaded playback source
   */
  unload () {
    // empty current playback
    emptySource.call(this);

    // empty the current playlist
    this.playlist.clearPlaylist();

    // empty the current quality profiles
    this.levels.clearLevels();

    // empty the current text tracks
    this.textTracks.clearTextTracks();

    // empty the tracking markers
    this.tracking.clear();

    // update the player state to empty
    this._state = STATE_EMPTY;
    this._hasPlayed = false;
    this._model.position = 0;
    this._model.duration = 0;

    this.emit(EVENT_UNLOAD);
  }

  /**
   * Loads a playlist item for playback
   *
   * @param {object} playlistItem The playlist item to load
   */
  loadSource (playlistItem) {
    this._logger.log('Player: loading a playlistItem object into the player', 'debug2');
    const tracker = this.tracking.createTracker(playlistItem);
    this.tracking.clear();
    this.tracking.setTracker(tracker);

    // empty the media element of its current playlist item
    emptySource.call(this);

    const isLive = (
      (playlistItem.metadata.contentType === METADATA_TYPE_ADSWIZZ_STREAM) ||
      (playlistItem.metadata.contentType === METADATA_TYPE_STREAM) ||
      (playlistItem.metadata.duration < 0)
    );

    changeMode(this, isLive ? 'live' : 'ondemand');

    // Update text tracks if the new playlistItem has them
    this.textTracks.setTextTracks(playlistItem.text);

    // update the available quality levels
    this.levels.setQualityLevelsFromPlaylistItem(playlistItem);

    // update the duration to the duration in the metadata if present
    if (!isLive && typeof playlistItem.metadata.duration === 'number') {
      this._model.duration = playlistItem.metadata.duration;

      this.emit({
        type: EVENT_DURATION_CHANGE,
        duration: this._model.duration
      });
    }

    this.emit(EVENT_LOADED);

    // If the playOnLoad flag is set then begin playback now
    if (this._playOnLoad) {
      this._logger.log('Player: Start playback once playlist item has loaded', 'debug2');
      this._playOnLoad = false;
      this.play();
    }
  }

  /**
   * Sets the source of the internal media element and begins loading audio data
   */
  startLoad () {
    // Loading should only be necessary in the idle state
    if (this._state !== STATE_IDLE && this._state !== STATE_ERROR) {
      return;
    }

    this.setState(STATE_WAITING);

    const source = this._model.source;
    const currentSource = this._model.currentSource;

    // Enable CORS on iono sources, but disable it everywhere else
    if (isIonoUrl(currentSource)) {
      // this._media.crossOrigin = 'use-credentials';
      this._media.crossOrigin = 'anonymous';
    } else {
      this._media.crossOrigin = null;
    }

    // Load HLS sources with Hls.js on unsupported browsers
    if (isHlsSource(source) && Hls.existsOn(this)) {
      Hls.startLoad(this);
    } else {
      this._logger.log(`Player: Begin loading audio source '${currentSource}'`, 'debug2');

      const playbackUrl = this.computed.replace(currentSource, encodeURIComponent);
      this._media.src = playbackUrl;
      this._media.load();
    }
  }

  /**
   * Returns the currently loaded playback source
   *
   * @return {object}
   */
  getSource () {
    return this._model.source;
  }

  /**
   * Returns the currently playing source url
   *
   * @return {string}
   */
  getCurrentSource () {
    return this._model.currentSource;
  }

  /**
   * Change the players current playback source
   *
   * @param {object|array|string} source
   */
  setSource (source) {
    const currentSource = this.getSource();

    // Only change source if the new source is different
    if (currentSource && source.url === currentSource.url) {
      return;
    } else if (currentSource) {
      // empty the current source
      emptySource.call(this);
    }

    // Update the player model
    this._model.source = source;
    this._model.currentSource = source.url;

    this.emit({
      type: EVENT_SOURCE_CHANGE,
      source: source,
      currentSource: source.url
    });

    // Check if the source is playable
    if (!source.mime) {
      this._logger.warn('[Player] Loading potentially unsupported playback source, a mime type could not be determined');
    } else if (!this.canPlaySource(source)) {
      // Error immediately if the source is not playable and a mime type is available
      return this.setError(new PlayerError(PlayerError.SRC_NOT_SUPPORTED));
    }

    this.setState(STATE_IDLE);

    if (this._options.preload) {
      this.startLoad();
    }
  }

  /**
   * Destroy the player instance
   * @todo implement destroy method on player
   * @todo unbind all audio event listeners
   */
  destroy () {
    this._logger.log('Player: Destroying player instance', 'debug2');

    emptySource.call(this);

    this.textTracks.destroy();
    this.playlist.destroy();
    this.tracking.destroy();
    this.levels.destroy();

    if (this._media) {
      // unbind media events
      Events.off(this._media, mediaEvents, this);

      // remove media element
      this._media = null;
    }

    // Unbind all events on the player
    Events.off(this);

    this.setState(STATE_EMPTY);
    this.emit(EVENT_DESTROY);
  }

  /**
   * Checks if the player supports playback of the desired format
   *
   * @param {string} format Playback source format
   *
   * @return {boolean}
   */
  canPlayFormat (format) {
    return canPlayFormat(format);
  }

  /**
   * Checks if the player supports playback of the desired source
   *
   * @param {object|string} source Playback source object or a playback url
   *
   * @return {boolean}
   */
  canPlaySource (source) {
    return canPlaySource(source);
  }

  /**
   * Checks if the player supports playback of the desired mime type
   *
   * @param {string} type Playback source mime type
   *
   * @return {boolean}
   */
  canPlayType (type) {
    return canPlayType(type);
  }

  /**
   * Checks if media playback has ended
   *
   * @return {Boolean}
   */
  isEnded () {
    return this._media.ended;
  }

  /**
   * Checks if the player is paused
   *
   * @return {boolean}
   */
  isPaused () {
    return this._model.paused;
  }

  /**
   * Checks if the player is playing a live source
   *
   * @return {boolean}
   */
  isLive () {
    return this._isLive || (this._media && (this._media.duration === -1 || this._media.duration === Infinity));
  }

  /**
   * Checks if the player is able to seek to the given position
   *
   * @param  {Number|null}  position
   *
   * @return {Boolean}
   */
  isSeekable (position = null) {
    if (this.isLive()) {
      return false;
    }

    const seekable = this.getSeekable();

    if (seekable.length === 0) {
      return false;
    }

    if (position === null) {
      return true;
    }

    return position >= seekable[0][0] && position <= seekable[seekable.length - 1][1];
  }

  /**
   * Returns the current playback position
   *
   * @return {number}
   */
  getPosition () {
    return this._model.currentTime;
  }

  /**
   * Returns the current playback duration
   *
   * @return {number}
   */
  getDuration () {
    return this.isLive() ? -1 : this._model.duration;
  }

  /**
   * Begin playback of the current source
   */
  play () {
    try {
      // Update the paused flag, when enough audio data is loaded and this flag
      // is set to false, then the player will automatically start playback
      this._model.paused = false;
      // this._isStopping = false;

      // Begin buffering of audio data when playing for the first time
      if (this._state === STATE_LOADING) {
        this._playOnLoad = true;
        return;
      }

      if (this._state === STATE_IDLE || this._state === STATE_ERROR) {
        this.startLoad();
        return;
      }

      if (this._canPlay) {
        // sometimes live streams stall which causes some browsers to play only the current buffer
        // so instead we force the browser to reconnect to the stream every time play is called
        if (this._stalled && this.isLive()) {
          streamReconnect.call(this);
        }
        play.call(this);
      }
    } catch (e) {
      this._logger.warn(`Player: Error attempting to start playback: ${e.message}`);
    }
  }

  /**
   * Pause playback of the current source
   */
  pause () {
    pause.call(this);

    // stop loading of stream data
    if (this.isLive()) {
      // stop loading hls data if hls.js is in use
      if (Hls.isAttached(this)) {
        Hls.stopLoad(this);
      } else {
        this.stopLoad();
      }
      this._canPlay = false;
      this.setState(STATE_IDLE);
    }
  }

  /**
   * Stop and rewind playback
   */
  stop () {
    // this._isStopping = true;

    if (!this.isPaused()) pause.call(this);

    // // schedule the stop event top fire next frame
    // setTimeout(_ => {
    //   if (!this._isStopping) return;

    // this._isStopping = false;
    this.emit(EVENT_STOP);

    // transition to the idle state so that the player knows to start loading the playback source again
    if (this._isLive) {
      // stop loading hls data if hls.js is in use
      if (Hls.isAttached(this)) {
        Hls.stopLoad(this);
      } else {
        this.stopLoad();
      }
      this._canPlay = false;
      this._hasPlayed = false;
      this.setState(STATE_IDLE);
    }

    // rewind to the start
    if (this._canPlay) {
      this._media.currentTime = 0;
    }
    // }, 1);
  }

  /**
   * Stop loading audio data
   */
  stopLoad () {
    this._media.src = emptySourceUrl;
    this._media.load();
  }

  /**
   * Returns the current playback rate
   *
   * @return {number}
   */
  getPlaybackRate () {
    return this._media.playbackRate;
  }

  /**
   * Change the players playback rate
   *
   * @param {number} rate
   */
  setPlaybackRate (rate) {
    if (this._model.playbackRate !== rate) {
      this._model.playbackRate = rate;

      // update the media elements playback rate immediately when not playing a live source
      if (!this.isLive()) {
        this._media.defaultPlaybackRate = this._media.playbackRate = rate;
      }
    }
  }

  /**
   * Retrieve the timeranges that have been buffered
   *
   * @return {array}
   */
  getBuffered () {
    if (this._model.buffered && this._model.buffered.length > 0) {
      return this._model.buffered;
    }
    return [];
  }

  /**
   * Retrieve an estimate of the percentage of the current playback file has been buffered.
   *
   * Because the browser buffers in time ranges this may not be completely accurate,
   * because it only buffers ranges the browser requests so if the user skips forward
   * it won't buffer anything in between.
   *
   * @return {number}
   */
  getBufferedPercentage () {
    const buffered = this.getBuffered();
    const duration = this.getDuration();

    if (buffered.length > 0 && duration > 0) {
      const bufferedPercent = buffered[buffered.length - 1][1] / duration * 100;
      return Math.min(100, Math.max(0, bufferedPercent));
    }

    return 0;
  }

  /**
   * Retrieve the seekable time ranges
   *
   * @return {TimeRange[]}
   */
  getSeekable () {
    return timeRangesToArray(this.getMedia().seekable);
  }

  /**
   * Seek backwards in time
   *
   * @param {number} seconds the total seconds to seek backward
   *
   * @todo move Player.backward from the core player to the public player
   */
  backward (seconds) {
    this.setPosition(this.getPosition() - seconds);
  }

  /**
   * Seek forwards in time
   *
   * @param {number} seconds the total seconds to seek forward
   *
   * @todo move Player.forward from the core player to the public player
   */
  forward (seconds) {
    this.setPosition(this.getPosition() + seconds);
  }

  /**
   * Seek to a new position in playback
   *
   * @param {number} seconds
   */
  setPosition (seconds) {
    // Prevent seeking when nothing is loaded
    if (this._state === STATE_EMPTY) {
      return -3;
    }

    // Prevent seeking on live sources
    if (this._isLive) {
      return -1;
    }

    // Begin buffering of audio data when playing for the first time
    if (this._state === STATE_IDLE) {
      this.startLoad();

      // record the position we wish to seek to so that once the player
      // has loaded enough audio data when can initiate the seek
      this._seekOnLoad = seconds;
      return;
    }

    // Wait for the playback media to become playable before seeking
    if (this._state === STATE_WAITING && !this._canPlay) {
      this._seekOnLoad = seconds;
      return;
    }

    // Prevent seeking past the end of the audio source
    if (seconds > this.getDuration()) {
      return -2;
    }

    // start the seek as long as the seek position is in the list of seekable time ranges
    this._media.currentTime = seconds;

    return seconds;
  }

  /**
   * Checks if the player is muted
   *
   * @return {boolean}
   */
  getMute () {
    return this._model.muted;
  }

  /**
   * Mute or unmute playback
   *
   * @param {boolean} muted
   */
  setMute (muted = true) {
    this._media.muted = muted;
  }

  /**
   * Change the players playback volume
   *
   * @param {number} volume
   */
  setVolume (volume) {
    this._media.volume = round(volume, 2);

    // unmute playback if currently muted
    if (this._media.muted) {
      this.setMute(false);
    }
  }

  /**
   * Returns the current playback volume
   *
   * @return {number}
   */
  getVolume () {
    return (this._media && this._media.volume) || 1;
  }

  /**
   * Returns the current preload setting
   *
   * @return {boolean|string}
   */
  getPreload () {
    return this._options.preload;
  }

  /**
   * Change the current preload setting
   *
   * @param {boolean} preload
   */
  setPreload (preload) {
    // determine the preload value to set on the audio element
    let audioPreload = preload;

    this._options.preload = preload;

    if (audioPreload === true) {
      audioPreload = 'auto';
    } else if (audioPreload === false) {
      audioPreload = 'none';
    }

    // update the preload property on the audio element
    this._media.preload = audioPreload;

    // if the preload setting has been changed to one that allows preloading then start loading audio data now
    if (audioPreload !== 'none') {
      this.startLoad();
    }
  }

  /**
   * Helpers
   */

  /**
   * Retrieve the media element bound to this player
   *
   * @return {HTMLAudioElement}
   */
  getMedia () {
    return this._media;
  }

  /**
   * Returns the current playback error
   *
   * @return {object}
   */
  getError () {
    return this._model.error;
  }

  /**
   * Triggers an error on a player instance
   *
   * @param {Error} error
   */
  setError (error) {
    this._model.error = error;
    this._model.paused = true;

    this.setState(STATE_ERROR);

    this.emit({
      type: EVENT_ERROR,
      error: error
    });
  }

  /**
   * Returns the current playback state
   *
   * @return {string}
   */
  getState () {
    return this._state;
  }

  /**
   * Change the players current state
   *
   * @param {string} newState
   */
  setState (newState) {
    if (this._state === newState) {
      return;
    }

    const prevState = this._state;

    this._state = newState;

    this.emit({
      type: EVENT_STATE,
      previousState: prevState,
      currentState: newState
    });

    switch (newState) {
      case STATE_EMPTY:
        this._canPlay = false;
        this.emit(EVENT_EMPTY);
        break;

      case STATE_LOADING:
        this._canPlay = false;
        this.emit(EVENT_LOADING);
        break;

      case STATE_PLAYING:
        this._canPlay = true;
        this._model.paused = false;
        this.emit(EVENT_PLAYING);
        break;

      case STATE_PAUSED:
        this._canPlay = true;
        this._model.paused = true;
        this.emit(EVENT_PAUSED);
        break;

      case STATE_WAITING:
        this._canPlay = false;
        this.emit(EVENT_WAITING);
        break;
    }
  }

  /**
   * Returns information about the current environment
   *
   * @return {object}
   */
  getEnvironment () {
    return Environment;
  }

  /**
   * Events
   */

  /**
   * Handles media events fired by the audio element
   *
   * @param {object} event
   */
  handleEvent (event) {
    const currentSrc = this._media ? this._media.currentSrc : '';
    if (isEmptySource(currentSrc) || this._state === STATE_EMPTY) {
      return;
    }

    this._logger.log(`Player: HTML5 Event: ${event.type}`, 'debug2');

    // prevent triggering seek events when playing a live stream
    if (this.isLive() && (event.type === 'seeking' || event.type === 'seeked')) {
      return false;
    }

    if (event.type === 'stalled') {
      this._stalled = true;
    }
    if (event.type === 'playing' || event.type === 'error' || event.type === 'suspend') {
      this._stalled = false;
    }

    if (event.type === 'error') {
      if (this._state === STATE_EMPTY || isEmptySource(currentSrc)) {
        return;
      }

      // When the player fails to play a source, try the next supported source in the list
      const currentLevel = this.levels.getCurrentLevel();

      let errorFatal = true;
      let error = event.error || new PlayerError(PlayerError.UNKNOWN);

      if (this._state !== STATE_LOADING && (currentLevel && currentLevel.sources && currentLevel.sources.length > 0)) {
        const currentSource = this.getSource();
        const currentSourceIndex = currentLevel.sources.indexOf(currentSource);
        const nextSourceIndex = currentSourceIndex + 1;

        // fall back to a different format in the same quality
        if (nextSourceIndex < currentLevel.sources.length) {
          this._logger.warn('Player: Playback error while buffering audio source, trying the next supported source.');

          errorFatal = false;

          const nextSource = currentLevel.sources[nextSourceIndex];

          // flags used to resume playback
          const seekOnLoad = this._seekOnLoad || this.getPosition();
          const playOnLoad = this._playOnLoad || !this.isPaused();

          this.stopLoad();
          // update and begin loading the fallback source
          this.setSource(nextSource);
          this.startLoad();

          // reset the playback flags to ensure playback resumes correctly
          this._playOnLoad = playOnLoad;
          this._seekOnLoad = seekOnLoad;

          return;
        }
      }

      if (this._media.error) {
        this._logger.warn(`Player: HTML5 playback error: [${this._media.error.code}] ${this._media.error.message}`);
        error = PlayerError.fromMediaError(this._media.error);
      }

      if (errorFatal) {
        this.setError(error);
      }

      return;
    }

    if (event.type === 'pause') {
      if (!this._model.paused) {
        this._logger.debug('!!! MEDIA PAUSED FROM ELSEWHERE !!!');

        this._model.paused = true;
        this.emit(EVENT_PAUSE);

        if (this.isLive() && this.getState() === STATE_PLAYING) {
          return this.setState(STATE_IDLE);
        }
      }

      if (this.getState() === STATE_PLAYING) {
        return this.setState(STATE_PAUSED);
      }
      return;
    }

    if (event.type === 'play') {
      if (this._model.paused) {
        this._logger.debug('!!! MEDIA PLAYED FROM ELSEWHERE !!!');

        if (this.isLive()) {
          return this.play();
        }
      }

      this._model.paused = false;
      this.emit(EVENT_PLAY);
      return;
    }

    if (event.type === 'volumechange') {
      this._model.volume = this._media.volume;

      // trigger the mute or unmute event if the audio elements muted state has been changed
      if (this._model.muted !== this._media.muted) {
        this._model.muted = this._media.muted;
        this.emit(this._model.muted ? EVENT_MUTE : EVENT_UNMUTE);
      }

      return this.emit({
        type: EVENT_VOLUME_CHANGE,
        volume: this._model.volume,
        muted: this._model.muted
      });
    }

    if (event.type === 'durationchange') {
      const mediaDuration = this._media ? this._media.duration : 0;
      const modelDuration = this._model.duration;
      const playlistItem = this.playlist.getPlaylistItem();
      const modelDurationRounded = round(modelDuration, 3);

      let mediaDurationRounded = round(mediaDuration, 3);

      if (isNaN(mediaDuration) || mediaDuration === Infinity || this.isLive()) {
        mediaDurationRounded = -1;
      }

      // keep the playlist duration if the media duration changes to an invalid value
      if (playlistItem.metadata.duration > 0 && mediaDurationRounded <= 0) {
        return;
      }

      // prevent emitting the durationchange event if the duration is the same as the playlist item duration
      if (mediaDurationRounded === modelDurationRounded) {
        return;
      }

      // update player model with the new duration
      this._model.duration = mediaDurationRounded;

      // prevent triggering duration change events when the audio duration is pretty much the same as the playlist item duration
      if (mediaDurationRounded !== -1 && Math.abs(modelDurationRounded - mediaDurationRounded) <= this._durationChangeThreshold) {
        return;
      }

      this._logger.log(`Player: Duration has changed to: ${mediaDurationRounded}`, 'debug2');

      if (playlistItem) {
        // update the duration of the current playlist item
        playlistItem.metadata.duration = mediaDurationRounded;
      }

      // If the source duration is infinity then self is a live audio source
      if (this._media && mediaDurationRounded === -1) {
        this._logger.log('Player: New duration indicates a live stream', 'debug2');
        changeMode(this, 'live');
      } else if (this._media && mediaDurationRounded > 0) {
        changeMode(this, 'ondemand');
      }

      return this.emit({
        type: EVENT_DURATION_CHANGE,
        duration: this._model.duration
      });
    }

    if (event.type === 'seeking') {
      return this.emit({
        type: EVENT_SEEK,
        position: this._media.currentTime
      });
    }

    if (event.type === 'timeupdate') {
      if (this._media) {
        let currTime = 0;
        const prevTime = this._model.currentTime;

        // trigger a progress event on timeupdate if the buffer amount has changed but hasn't been updated in the model yet
        const timeRanges = (this._media && this._media.buffered.length > 0) ? this._media.buffered : [];
        if (
          (timeRanges.length !== this._model.buffered.length) ||
          (timeRanges.length && this._model.buffered.length && timeRanges.end(timeRanges.length - 1) !== this._model.buffered[this._model.buffered.length - 1][1])
        ) {
          this._model.buffered = timeRangesToArray(timeRanges);

          this.emit({
            type: EVENT_PROGRESS,
            buffered: this.getBuffered(),
            bufferedPercentage: this.getBufferedPercentage()
          });
        }

        if (this.isLive()) {
          if (this._streamStartTime === null) {
            this._streamStartTime = this._media.currentTime;
          }
          currTime = round(this._media.currentTime - this._streamStartTime, 2);
        } else {
          currTime = round(this._media.currentTime, 2);
        }

        if (currTime === prevTime) {
          return;
        }

        this._model.currentTime = Math.max(0, currTime);
      } else {
        this._model.currentTime = 0;
      }

      return this.emit({
        type: EVENT_POSITION,
        position: this.getPosition(),
        duration: this.getDuration()
      });
    }

    if (event.type === 'ratechange') {
      return this.emit({
        type: EVENT_PLAYBACK_RATE_CHANGE,
        playbackRate: this._media.playbackRate
      });
    }

    if (event.type === 'waiting' && this._state !== 'loading') {
      return this.setState(STATE_WAITING);
    }

    if (event.type === 'canplay' || event.type === 'canplaythrough') {
      this._canPlay = true;
      // free up the stream connect attempts counter for the next stall event
      this._streamReconnectAttempts = 0;
    }

    if (event.type === 'loadeddata' || event.type === 'loadedmetadata') {
      if (!this._canPlay) {
        this._canPlay = true;
      }

      if (this._state === STATE_WAITING) {
        // resume playback if playOnLoad is true or when the player paused flag is true and does not match the media element
        if (this._playOnLoad || (!this._model.paused && this._model.paused !== this._media.paused)) {
          this._playOnLoad = false;
          play.call(this);
        }

        // transition to the paused state
        if (this._model.paused) {
          this.setState(STATE_PAUSED);
        }

        // resume from current position in cases where the quality level has been changed
        if (this._seekOnLoad > 0) {
          this.setPosition(this._seekOnLoad);
          this._seekOnLoad = 0;
        }
      }
    }

    if (event.type === 'playing') {
      return this.setState(STATE_PLAYING);
    }

    if (event.type === 'emptied') {
      this._model.currentTime = 0;
      return;
    }

    if (event.type === 'loadstart') {
      if (!this._media || isEmptySource(this._media.src)) {
        return;
      }

      this._canPlay = false;
      this._stalled = false;
      this._model.error = false;
      this.emit({
        type: EVENT_LOAD_START,
        source: this.getSource().url
      });
      return this.setState(STATE_WAITING);
    }

    if (event.type === 'progress') {
      const timeRanges = (this._media && this._media.buffered.length > 0) ? this._media.buffered : [];
      if (
        (timeRanges.length !== this._model.buffered.length) ||
        (timeRanges.length && this._model.buffered.length && timeRanges.end(timeRanges.length - 1) !== this._model.buffered[this._model.buffered.length - 1][1])
      ) {
        this._model.buffered = timeRangesToArray(timeRanges);

        return this.emit({
          type: EVENT_PROGRESS,
          buffered: this.getBuffered(),
          bufferedPercentage: this.getBufferedPercentage()
        });
      }
    }

    // when the current item finishes playback, start the next playlist item unless the repeat mode is set to single
    if (event.type === 'ended') {
      this._model.paused = true;
      // prevent complete events on live streams
      if (this.isLive()) return;
      return this.emit({
        type: EVENT_COMPLETE,
        position: this.getPosition(),
        duration: this.getDuration()
      });
    }

    this.emit(event.type);
  }
}

export default Player;
